import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getUserSubscriptionById, activateUserSubscription, deactivateUserSubscription } from 'helpers/fakebackend_helper';
import { Badge, Button, Spinner } from 'reactstrap';

const SubscriptionManagement = ({ userId, userName }) => {
    const queryClient = useQueryClient();
    const [isActivating, setIsActivating] = useState(false);
    const [isDeactivating, setIsDeactivating] = useState(false);

    const { data: subscriptionData, isLoading, error } = useQuery(
        ['user_subscription_management', userId],
        () => getUserSubscriptionById(userId),
        {
            enabled: !!userId,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
        }
    );

    const activateMutation = useMutation(
        () => activateUserSubscription(userId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['user_subscription_management', userId]);
                queryClient.invalidateQueries(['user_subscription_badge', userId]);
                setIsActivating(false);
            },
            onError: (error) => {
                console.error('Failed to activate subscription:', error);
                setIsActivating(false);
            }
        }
    );

    const deactivateMutation = useMutation(
        () => deactivateUserSubscription(userId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['user_subscription_management', userId]);
                queryClient.invalidateQueries(['user_subscription_badge', userId]);
                setIsDeactivating(false);
            },
            onError: (error) => {
                console.error('Failed to deactivate subscription:', error);
                setIsDeactivating(false);
            }
        }
    );

    const handleActivate = () => {
        if (window.confirm(`Are you sure you want to activate subscription for ${userName}?`)) {
            setIsActivating(true);
            activateMutation.mutate();
        }
    };

    const handleDeactivate = () => {
        if (window.confirm(`Are you sure you want to deactivate subscription for ${userName}?`)) {
            setIsDeactivating(true);
            deactivateMutation.mutate();
        }
    };

    if (isLoading) {
        return (
            <div className="d-flex align-items-center">
                <Spinner size="sm" className="me-2" />
                <span>Loading...</span>
            </div>
        );
    }

    if (error) {
        return <Badge color="danger">Error</Badge>;
    }

    const hasActiveSubscription = subscriptionData?.has_subscription;
    const subscription = subscriptionData?.subscription;

    return (
        <div className="d-flex flex-column gap-2">
            {/* Status Badge */}
            <div>
                {!hasActiveSubscription ? (
                    <Badge color="danger">No Subscription</Badge>
                ) : (
                    <Badge color={subscription?.status === 'active' ? 'success' : 'warning'}>
                        {subscription?.status?.charAt(0).toUpperCase() + subscription?.status?.slice(1)}
                    </Badge>
                )}
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-1">
                {!hasActiveSubscription ? (
                    <Button
                        color="success"
                        size="sm"
                        onClick={handleActivate}
                        disabled={isActivating}
                    >
                        {isActivating ? (
                            <>
                                <Spinner size="sm" className="me-1" />
                                Activating...
                            </>
                        ) : (
                            'Activate'
                        )}
                    </Button>
                ) : (
                    <Button
                        color="danger"
                        size="sm"
                        onClick={handleDeactivate}
                        disabled={isDeactivating}
                    >
                        {isDeactivating ? (
                            <>
                                <Spinner size="sm" className="me-1" />
                                Deactivating...
                            </>
                        ) : (
                            'Deactivate'
                        )}
                    </Button>
                )}
            </div>

            {/* Subscription Details */}
            {hasActiveSubscription && subscription && (
                <div className="small text-muted">
                    {subscription.current_period_end && (
                        <div>
                            Until: {new Date(subscription.current_period_end).toLocaleDateString()}
                        </div>
                    )}
                    {subscription.metadata?.activated_by_admin && (
                        <div className="text-info">
                            Admin Activated
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

SubscriptionManagement.propTypes = {
    userId: PropTypes.number.isRequired,
    userName: PropTypes.string.isRequired,
};

export default SubscriptionManagement;


